import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, Lightbulb } from "lucide-react";
import { AffiliateCard } from "@/components/AffiliateCard";
import { Footer } from "@/components/rentalmnp/shared/Footer";
import { Logo } from "@/components/rentalmnp/shared/Logo";

const TITLE = "Nos partenaires de confiance | RentaLMNP";
const DESC =
  "Découvrez les partenaires recommandés par RentaLMNP pour optimiser votre investissement LMNP : crédit immobilier, assurance emprunteur.";

export const Route = createFileRoute("/partenaires")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://rentalmnp.com/partenaires" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://rentalmnp.com/partenaires" }],
  }),
  component: PartenairesPage,
});

function PartenairesPage() {
  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-4xl px-6 py-8 lg:px-12">
        <div className="mb-6 flex items-center justify-between">
          <Logo size={36} />
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Link>
        </div>

        <header className="mt-6 mb-10">
          <h1 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Nos partenaires de confiance
          </h1>
          <p className="mt-3 text-base text-muted-foreground sm:text-lg">
            Nous ne recommandons que des services que nous avons testés et qui
            apportent une vraie valeur aux investisseurs LMNP.
          </p>
        </header>

        <section aria-labelledby="financement" className="space-y-6">
          <div>
            <h2
              id="financement"
              className="text-2xl font-bold text-foreground"
            >
              Financement
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              <strong className="font-semibold text-foreground">
                Meilleurtaux
              </strong>{" "}
              est l'un des principaux courtiers en crédit immobilier et
              assurance de prêt en France. Plus de 20 ans d'expérience et 150+
              banques partenaires pour négocier les meilleures conditions de
              votre projet LMNP.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <AffiliateCard variant="credit" placement="partners-page" />
            <AffiliateCard variant="assurance" placement="partners-page" />
          </div>
        </section>

        <section
          aria-labelledby="transparence"
          className="mt-14 rounded-2xl border border-border bg-secondary/40 p-6 sm:p-8"
        >
          <h2 id="transparence" className="flex items-center gap-2 text-xl font-bold text-foreground">
            <Lightbulb className="h-5 w-5 text-warning" aria-hidden />
            Comment ça marche ?
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            RentaLMNP est gratuit grâce aux commissions versées par nos
            partenaires lorsque vous souscrivez un service via nos liens. Cela
            ne change absolument rien pour vous (même prix, mêmes conditions),
            et nous permet de maintenir le simulateur gratuit et ouvert à tous.
          </p>
          <p className="mt-4 text-sm font-medium text-foreground">
            Nous ne recommandons que des partenaires :
          </p>
          <ul className="mt-3 space-y-2 text-sm text-foreground/90">
            {[
              "Régulés et reconnus sur le marché français",
              "Testés et approuvés par notre équipe",
              "Avec des avis clients vérifiés positifs",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            Vous restez 100% libre de comparer ailleurs.{" "}
            <Link
              to="/mentions-legales"
              className="font-medium text-primary hover:underline"
            >
              Voir nos mentions légales
            </Link>
            .
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
